import { AlertTriangle, PackageCheck, PackageSearch, RotateCcw, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

import type {
  InferredNotReturnedRow,
  ReplacementRateRow,
  RmaDispositionRow,
  RmaFlowPayloadWithDisposition,
} from "@busright/data-contract";
import { COLORS, RAG } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { DataTable } from "@/components/DataTable";
import { Distribution } from "@/components/Distribution";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import { InsightCallout } from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { MetricLabel } from "@/components/MetricTooltip";
import { PageHeader } from "@/components/PageHeader";
import { useScope } from "@/context/ScopeContext";
import { useRmaFlow } from "@/hooks/queries";
import { formatInt, formatPct } from "@/lib/format";

// RMA / Returns — the end-of-life / asset-return story (R7-R9). Backed by /api/rma, which the
// hardware-hub-api serves via `getRmaFlowWithDisposition` (RmaFlowPayload widened additively with
// the disposition + refurb-rate block — see rma-disposition.ts). `useRmaFlow`'s contract type is the
// base RmaFlowPayload; the live endpoint already returns the wider shape, so we narrow to it here
// rather than fabricating a second hook. If the endpoint ever serves the lean shape the optional
// `disposition` access degrades to the empty state below (no fabricated numbers).
//
// Two honesty caveats surfaced verbatim from the payload: `inferenceCaveat` (RMAs carry no
// device/serial ref and no real label-sent/return dates — the "label sent, not returned" rows are
// an INFERRED proxy) and `dispositionCaveat` (disposition/refurb-rate is a rate over the
// returned-and-RMA'd subset only, not the whole churned fleet).

const DISPOSITION_LABEL: Record<RmaDispositionRow["disposition"], string> = {
  RESTOCK: "Restock (A-stock)",
  SCRAP: "Scrap / eCycled",
  PENDING: "Pending",
  OTHER: "Other",
};

const DISPOSITION_COLOR: Record<RmaDispositionRow["disposition"], string> = {
  RESTOCK: RAG.OK,
  SCRAP: RAG.CRITICAL,
  PENDING: COLORS.textSecondary,
  OTHER: COLORS.textMuted,
};

const dimensionLabel = (dim: ReplacementRateRow["dimension"]): string => {
  if (dim === "district") {
    return "By district";
  }
  if (dim === "time") {
    return "By month";
  }
  return "By reason";
};

// Confidence is stored 0..1 on the contract; render it as the same 0..100 percentage every other
// confidence figure in the hub uses (see LcReplacementAge's identical convention).
const CONFIDENCE_SCALE = 100;
const CONFIDENCE_DIGITS = 0;
const confidencePct = (c: number): string => formatPct(c * CONFIDENCE_SCALE, CONFIDENCE_DIGITS);

// One disposition row: label + count/share, colored by disposition (RESTOCK reads OK-green,
// SCRAP reads CRITICAL-red — this is a genuine repurpose/scrap outcome, not a health score, but the
// RAG palette reads intuitively here so we reuse the tokens rather than inventing new ones).
const DispositionRow = ({ row }: { readonly row: RmaDispositionRow }): ReactNode => (
  <li
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontSize: 12,
      padding: "3px 0",
      color: DISPOSITION_COLOR[row.disposition],
    }}
  >
    <span>{DISPOSITION_LABEL[row.disposition]}</span>
    <span className="br-tabular-nums">
      {formatInt(row.rmaCount)} ({formatPct(row.sharePct)})
    </span>
  </li>
);

// The disposition chart's legend + caveat footer, split out to keep RmaReturnsPage's cognitive
// complexity within the project's Biome budget.
const DispositionPanel = ({
  disposition,
  loading,
}: {
  readonly disposition: RmaFlowPayloadWithDisposition["disposition"] | null;
  readonly loading: boolean;
}): ReactNode => (
  <ChartCard
    title="Disposition"
    subtitle="Restock (repurposable) vs scrap vs pending"
    source="rma.disposition"
    loading={loading}
  >
    {disposition && disposition.rows.length > 0 ? (
      <Distribution
        bins={disposition.rows.map((r) => ({
          label: DISPOSITION_LABEL[r.disposition],
          count: r.rmaCount,
        }))}
        riskColor={COLORS.accent}
      />
    ) : (
      <EmptyState title="No disposition data" />
    )}
    {disposition ? (
      <ul style={{ marginTop: 10, listStyle: "none", padding: 0 }}>
        {disposition.rows.map((r) => (
          <DispositionRow key={r.disposition} row={r} />
        ))}
      </ul>
    ) : null}
    {disposition ? (
      <p style={{ fontSize: 11, color: "var(--br-text-muted)", marginTop: 8 }}>
        {disposition.dispositionCaveat}
      </p>
    ) : null}
  </ChartCard>
);

// The inferred not-returned worklist, split out for the same complexity-budget reason as above.
const NotReturnedPanel = ({
  rows,
  inferenceCaveat,
  loading,
}: {
  readonly rows: readonly InferredNotReturnedRow[];
  readonly inferenceCaveat: string | undefined;
  readonly loading: boolean;
}): ReactNode => (
  <ChartCard
    title="Label sent, not returned (inferred)"
    subtitle="Outbound return-label orders with no matching return after the follow-up window — flag inference, not a record"
    source="rma.inferredNotReturned"
    loading={loading}
  >
    {rows.length > 0 ? (
      <>
        <DataTable<InferredNotReturnedRow>
          columns={[
            { key: "recipient", header: "Recipient", render: (r) => r.recipientName },
            {
              key: "order",
              header: "Order #",
              render: (r) => <span className="mono">{r.orderNbr ?? "—"}</span>,
            },
            { key: "sku", header: "SKU", render: (r) => r.skuDescription ?? "—" },
            {
              key: "sent",
              header: "Inferred label sent",
              render: (r) => <span className="mono">{r.inferredLabelSentDate ?? "—"}</span>,
            },
            {
              key: "outstanding",
              header: "Business days outstanding",
              align: "right",
              render: (r) => (
                <span className="mono br-tabular-nums">
                  {r.businessDaysOutstanding === null ? "—" : formatInt(r.businessDaysOutstanding)}
                </span>
              ),
            },
            {
              key: "confidence",
              header: "Confidence",
              align: "right",
              render: (r) => (
                <span className="mono br-tabular-nums" style={{ color: "var(--br-text-muted)" }}>
                  {confidencePct(r.confidence)} · {r.matchMethod}
                </span>
              ),
            },
          ]}
          rows={rows}
          rowKey={(r) =>
            `${r.orderNbr ?? "row"}-${r.recipientName}-${r.inferredLabelSentDate ?? "?"}`
          }
          emptyTitle="No inferred not-returned rows"
        />
        <p style={{ fontSize: 11, color: "var(--br-text-muted)", marginTop: 10 }}>
          {inferenceCaveat}
        </p>
      </>
    ) : (
      <EmptyState
        title="No inferred not-returned rows"
        message={
          inferenceCaveat ?? "RMAs carry no device/serial ref and no real label/return dates."
        }
      />
    )}
  </ChartCard>
);

export const RmaReturnsPage = (): ReactNode => {
  const { data, isLoading, isError, error } = useRmaFlow();
  const { scope } = useScope();
  const meta = data?.meta;
  // The /api/rma endpoint serves the disposition-widened payload; the hook is typed to match, so no
  // cast is needed. Optional chaining below still guards a lean-shape response.
  const payload = data?.data;

  const openTotal = payload?.openByAge.reduce((sum, b) => sum + b.count, 0) ?? 0;
  const resolvedTotal = payload?.resolutionBuckets.reduce((sum, b) => sum + b.count, 0) ?? 0;
  const notReturned = payload?.inferredNotReturned ?? [];
  const disposition = payload?.disposition ?? null;
  const refurbRatePct = disposition?.refurbRatePct ?? null;

  const byDistrict = payload?.replacementRate.filter((r) => r.dimension === "district") ?? [];
  const byTime = payload?.replacementRate.filter((r) => r.dimension === "time") ?? [];
  const byReason = payload?.replacementRate.filter((r) => r.dimension === "reason") ?? [];
  const replacementSections: readonly {
    readonly dim: ReplacementRateRow["dimension"];
    readonly rows: readonly ReplacementRateRow[];
  }[] = [
    { dim: "district", rows: byDistrict },
    { dim: "time", rows: byTime },
    { dim: "reason", rows: byReason },
  ];

  if (isError) {
    return (
      <>
        <PageHeader title="RMA / Returns" meta={meta} />
        <ChartCard title="RMA / Returns">
          <EmptyState
            title="Couldn't load RMA / Returns"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="RMA / Returns"
        description="Open/aging RMAs, disposition, refurb rate, and the inferred label-sent-not-returned worklist. Facts and flow — no RAG."
        meta={meta}
      />

      {disposition ? (
        <InsightCallout
          headline={`${formatPct(refurbRatePct ?? 0)} of RMAs come back A-stock / repurposable, over ${formatInt(disposition.totalRmas)} RMAs total.`}
          stats={[
            { label: "Refurb rate", value: formatPct(refurbRatePct ?? 0) },
            { label: "Open RMAs", value: formatInt(openTotal) },
            { label: "Inferred not-returned", value: formatInt(notReturned.length) },
          ]}
          severity="neutral"
          icon={PackageCheck}
        />
      ) : null}

      <div className="grid kpi-band">
        <Drillable metricKey="rma.openCount" scope={scope} label="Drill into open RMAs">
          <KpiCard
            label={<MetricLabel metricKey="rma.openCount" />}
            value={formatInt(openTotal)}
            icon={PackageSearch}
            source="rma.openByAge"
            loading={isLoading}
          />
        </Drillable>
        <Drillable metricKey="rma.agingDays" scope={scope} label="Drill into RMA aging">
          <KpiCard
            label="Aging RMAs"
            value={formatInt(payload?.agingBuckets.reduce((sum, b) => sum + b.count, 0) ?? 0)}
            icon={AlertTriangle}
            source="rma.agingBuckets"
            loading={isLoading}
          />
        </Drillable>
        <KpiCard
          label="Resolved RMAs"
          value={formatInt(resolvedTotal)}
          icon={RotateCcw}
          source="rma.resolutionBuckets"
          loading={isLoading}
        />
        <KpiCard
          label="Refurb rate (A-stock)"
          value={disposition ? formatPct(refurbRatePct ?? 0) : "—"}
          icon={Trash2}
          source="rma.disposition (n small — see caveat)"
          loading={isLoading}
        />
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="Open by age"
          subtitle="RMAs currently open, bucketed by how long they've been open"
          source="rma.openByAge"
          loading={isLoading}
        >
          {payload && payload.openByAge.length > 0 ? (
            <Distribution
              bins={payload.openByAge.map((b) => ({ label: b.label, count: b.count }))}
            />
          ) : (
            <EmptyState title="No open RMAs" />
          )}
        </ChartCard>

        <ChartCard
          title="Resolution"
          subtitle="Closed RMAs by resolution outcome"
          source="rma.resolutionBuckets"
          loading={isLoading}
        >
          {payload && payload.resolutionBuckets.length > 0 ? (
            <Distribution
              bins={payload.resolutionBuckets.map((b) => ({ label: b.label, count: b.count }))}
            />
          ) : (
            <EmptyState title="No resolved RMAs" />
          )}
        </ChartCard>

        <DispositionPanel disposition={disposition} loading={isLoading} />
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        {replacementSections.map((section) => (
          <ChartCard
            key={section.dim}
            title={`Replacement rate — ${dimensionLabel(section.dim)}`}
            subtitle="Replacements ÷ installed base, per slice"
            source="rma.replacementRate"
            loading={isLoading}
          >
            {section.rows.length > 0 ? (
              <Drillable
                metricKey="device.replacementRatePct"
                scope={scope}
                label={`Drill into replacement rate ${dimensionLabel(section.dim).toLowerCase()}`}
              >
                <DataTable
                  columns={[
                    { key: "label", header: dimensionLabel(section.dim), render: (r) => r.label },
                    {
                      key: "rate",
                      header: "Rate",
                      align: "right",
                      render: (r) => (
                        <span className="mono br-tabular-nums">
                          {formatPct(r.replacementRatePct)}
                        </span>
                      ),
                    },
                    {
                      key: "count",
                      header: "Replacements / base",
                      align: "right",
                      render: (r) => (
                        <span className="mono br-tabular-nums">
                          {formatInt(r.replacementCount)} / {formatInt(r.baseCount)}
                        </span>
                      ),
                    },
                  ]}
                  rows={section.rows}
                  rowKey={(r) => `${r.dimension}-${r.key}`}
                />
              </Drillable>
            ) : (
              <EmptyState title="No replacement-rate rows" />
            )}
          </ChartCard>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <NotReturnedPanel
          rows={notReturned}
          inferenceCaveat={payload?.inferenceCaveat}
          loading={isLoading}
        />
      </div>
    </>
  );
};
